import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, Users, Video, ChevronLeft, ChevronRight, ArrowRight, Mic2, GraduationCap, Image, Play, Pause, UserCheck, ExternalLink, Lock, X, Maximize2 } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import { API } from '@/App';

const typeConfig = {
  conference: { label: 'Congrès', icon: Mic2, color: '#35AFE4', bg: '#eaf6fc' },
  webinar: { label: 'Webinaire', icon: Video, color: '#7c3aed', bg: '#f3e8ff' },
  training: { label: 'Formation', icon: GraduationCap, color: '#059669', bg: '#d1fae5' },
  formation: { label: 'Formation', icon: GraduationCap, color: '#059669', bg: '#d1fae5' },
  workshop: { label: 'Atelier', icon: Users, color: '#d97706', bg: '#fef3c7' },
};

const formatDateFr = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const isUpcoming = (d) => d ? new Date(d) >= new Date(new Date().toDateString()) : false;

const PRO_ROLES = ['super_admin', 'regional', 'ophthalmologist', 'tresorier', 'auteur'];

// ==================== LIGHTBOX / SLIDESHOW COMPONENT ====================
const GalleryLightbox = ({ photos, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const baseUrl = API.replace('/api', '');

  const goNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % photos.length);
  }, [photos.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  // Auto-slideshow
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(goNext, 4000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, goNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === ' ') { e.preventDefault(); setIsPlaying(p => !p); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, goNext, goPrev]);

  const imgSrc = photos[currentIndex]?.startsWith('/') ? `${baseUrl}${photos[currentIndex]}` : photos[currentIndex];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="gallery-lightbox">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />

      {/* Close button */}
      <button onClick={onClose} className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors" data-testid="lightbox-close">
        <X size={20} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 z-50 px-3 py-1.5 bg-white/10 rounded-full text-white text-sm font-medium backdrop-blur-sm" data-testid="lightbox-counter">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Auto-play toggle */}
      <button
        onClick={() => setIsPlaying(p => !p)}
        className="absolute top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium backdrop-blur-sm flex items-center gap-2 transition-colors"
        data-testid="lightbox-autoplay"
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
        {isPlaying ? 'Pause' : 'Diaporama'}
      </button>

      {/* Navigation arrows */}
      {photos.length > 1 && (
        <>
          <button onClick={goPrev} className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors" data-testid="lightbox-prev">
            <ChevronLeft size={24} />
          </button>
          <button onClick={goNext} className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors" data-testid="lightbox-next">
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Image */}
      <div className="relative z-40 max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={imgSrc}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain transition-opacity duration-300"
          data-testid="lightbox-image"
        />
      </div>

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-1.5 max-w-[80vw] overflow-x-auto px-3 py-2 bg-black/40 rounded-full backdrop-blur-sm" data-testid="lightbox-thumbnails">
          {photos.map((photo, i) => {
            const thumbSrc = photo.startsWith('/') ? `${baseUrl}${photo}` : photo;
            return (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`flex-shrink-0 w-10 h-10 rounded-md overflow-hidden border-2 transition-all ${i === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
              >
                <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== GALLERY TAB CONTENT ====================
const GalleryTabContent = ({ eventId, user }) => {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const token = localStorage.getItem('token');
  const baseUrl = API.replace('/api', '');

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/events/${eventId}/gallery`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setGallery(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [eventId, token]);

  const isProMember = user && PRO_ROLES.includes(user.role);

  // Not logged in or not pro
  if (!user || !isProMember) {
    return (
      <div className="bg-white rounded-xl p-8 sm:p-12 shadow-sm text-center" data-testid="gallery-locked">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <Lock size={28} className="text-gray-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
          Galerie réservée aux membres
        </h2>
        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
          Les photos et vidéos des événements passés sont accessibles uniquement aux membres professionnels de la SFOALC.
        </p>
        {!user ? (
          <Link to="/auth" className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#35AFE4] text-white rounded-full font-semibold text-sm hover:bg-[#1e9bd4] transition-colors" data-testid="gallery-login-btn">
            <UserCheck size={16} /> Se connecter
          </Link>
        ) : (
          <p className="text-xs text-gray-400">Votre compte ne dispose pas d'un accès professionnel.</p>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm text-center">
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  const photos = gallery?.photos || [];
  const videos = gallery?.videos || [];
  const recapVideo = gallery?.recap_video_url || '';
  const hasContent = photos.length > 0 || videos.length > 0 || recapVideo;

  if (!hasContent) {
    return (
      <div className="bg-white rounded-xl p-8 sm:p-12 shadow-sm text-center" data-testid="gallery-empty">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#eaf6fc] flex items-center justify-center">
          <Image size={28} className="text-[#35AFE4]" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
          Galerie en préparation
        </h2>
        <p className="text-gray-500 text-sm">Les photos et vidéos de cet événement seront bientôt disponibles.</p>
      </div>
    );
  }

  // Convert YouTube URL to embed URL
  const getYoutubeEmbedUrl = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return url;
  };

  return (
    <div className="space-y-6">
      {/* Video recap */}
      {recapVideo && (
        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="gallery-video-section">
          <div className="flex items-center gap-2 mb-5">
            <Video size={20} className="text-[#35AFE4]" />
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Vidéo résumé</h2>
          </div>
          <div className="aspect-video rounded-xl overflow-hidden shadow-md bg-black">
            <iframe
              src={getYoutubeEmbedUrl(recapVideo)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Vidéo résumé"
            />
          </div>
        </div>
      )}

      {/* Additional videos */}
      {videos.length > 0 && (
        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="gallery-videos-list">
          <div className="flex items-center gap-2 mb-5">
            <Video size={20} className="text-[#35AFE4]" />
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Vidéos</h2>
            <span className="text-xs text-gray-400 ml-1">({videos.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map((vid, i) => (
              <div key={i} className="aspect-video rounded-xl overflow-hidden shadow-md bg-black">
                <iframe src={getYoutubeEmbedUrl(vid)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={`Video ${i + 1}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="gallery-photos-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Image size={20} className="text-[#35AFE4]" />
              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Galerie photos</h2>
              <span className="text-xs text-gray-400 ml-1">({photos.length} photos)</span>
            </div>
            <button
              onClick={() => setLightboxIndex(0)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#eaf6fc] text-[#35AFE4] rounded-full text-xs font-semibold hover:bg-[#d0ecf4] transition-colors"
              data-testid="start-slideshow-btn"
            >
              <Play size={12} /> Diaporama
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((photo, i) => {
              const src = photo.startsWith('/') ? `${baseUrl}${photo}` : photo;
              return (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className="group relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#35AFE4]"
                  data-testid={`gallery-photo-${i}`}
                >
                  <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      <Maximize2 size={14} className="text-gray-800" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <GalleryLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [regError, setRegError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/events/${id}`)
      .then(r => setEvent(r.data))
      .catch(() => navigate('/events'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleRegister = async () => {
    if (!user) { navigate('/auth'); return; }
    setRegistering(true);
    setRegError('');
    try {
      await axios.post(`${API}/events/${id}/register`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRegistered(true);
    } catch (e) {
      setRegError(e.response?.data?.detail || "Erreur lors de l'inscription");
    } finally { setRegistering(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" /></div>
  );
  if (!event) return null;

  const cfg = typeConfig[event.event_type] || typeConfig.conference;
  const Icon = cfg.icon;
  const upcoming = isUpcoming(event.end_date || event.start_date);
  const isSameDay = event.start_date && event.end_date &&
    new Date(event.start_date).toDateString() === new Date(event.end_date).toDateString();
  const dateRange = isSameDay || !event.end_date
    ? formatDateFr(event.start_date) : `${formatDateFr(event.start_date)} — ${formatDateFr(event.end_date)}`;
  const program = event.program || [];
  const speakers = event.speakers || [];
  const hasProgram = program.length > 0;
  const hasSpeakers = speakers.length > 0;

  // Gallery tab visible only for past events
  const showGalleryTab = !upcoming;

  const tabs = [
    { id: 'details', label: 'Détails' },
    ...(hasProgram ? [{ id: 'program', label: 'Programme' }] : []),
    ...(hasSpeakers ? [{ id: 'speakers', label: 'Intervenants' }] : []),
    ...(showGalleryTab ? [{ id: 'galerie', label: 'Galerie', icon: Image }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 pt-16 lg:pt-[72px]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2 text-sm">
          <Link to="/events" className="text-[#35AFE4] hover:underline flex items-center gap-1" data-testid="back-to-events"><ChevronLeft size={16} /> Agenda</Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600 truncate">{event.title}</span>
        </div>
      </div>

      {/* Hero with poster */}
      <div className="relative overflow-hidden" style={{ minHeight: '360px' }}>
        {event.poster_image ? (
          <>
            <img src={event.poster_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)' }} />
          </>
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${cfg.color}, #1e3a5f)` }} />
        )}

        {/* Video play button */}
        {event.video_url && upcoming && (
          <a href={event.video_url.replace('/embed/', '/watch?v=')} target="_blank" rel="noopener noreferrer" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-16 h-16 rounded-full bg-white/90 flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-2xl" data-testid="play-video-btn">
            <Play size={28} className="text-gray-900 ml-1" fill="currentColor" />
          </a>
        )}

        <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 sm:py-20 flex flex-col justify-end" style={{ minHeight: '360px' }}>
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full text-white" style={{ background: cfg.color }} data-testid="event-type-badge">
              <Icon size={12} /> {cfg.label}
            </span>
            {upcoming ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-500/90 text-white text-xs font-bold rounded-full">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> À venir
              </span>
            ) : (
              <span className="px-2.5 py-1 bg-gray-500/80 text-white text-xs font-bold rounded-full">Terminé</span>
            )}
            {event.location_type === 'online' && <span className="px-2.5 py-1 bg-purple-500/80 text-white text-xs font-bold rounded-full">En ligne</span>}
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 max-w-3xl" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="event-title">
            {event.title}
          </h1>
          <div className="flex flex-wrap gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {dateRange}</span>
            {event.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {event.location}</span>}
            {event.max_participants && <span className="flex items-center gap-1.5"><Users size={14} /> {event.max_participants} places</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 flex gap-0 overflow-x-auto" data-testid="event-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === tab.id ? 'text-[#35AFE4] border-[#35AFE4]' : 'text-gray-500 border-transparent hover:text-gray-700'}`} data-testid={`tab-${tab.id}`}>
              {tab.icon && <tab.icon size={14} />}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main content */}
          <div className="lg:col-span-2">

            {/* Tab: Details */}
            {activeTab === 'details' && (
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="tab-details-content">
                <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>À propos de cet événement</h2>
                <div className="text-gray-600 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: event.description }} />

                {event.video_url && upcoming && (
                  <div className="mt-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>Vidéo de présentation</h3>
                    <div className="aspect-video rounded-xl overflow-hidden shadow-md" data-testid="event-video">
                      <iframe src={event.video_url} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title="Video" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Programme */}
            {activeTab === 'program' && hasProgram && (
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="tab-program-content">
                <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Barlow', sans-serif" }}>Programme</h2>
                <div className="relative">
                  <div className="absolute left-[18px] top-2 bottom-2 w-px bg-gray-200" />
                  <div className="space-y-0">
                    {program.map((item, i) => (
                      <div key={i} className="flex gap-4 items-start py-3 relative" data-testid={`program-item-${i}`}>
                        <div className="relative z-10 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ background: item.speaker ? cfg.bg : '#f3f4f6', color: item.speaker ? cfg.color : '#9ca3af' }}>
                          <Clock size={14} />
                        </div>
                        <div className="flex-1 min-w-0 pb-3 border-b border-gray-50">
                          <div className="flex items-baseline gap-3">
                            <span className="text-sm font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>{item.time}</span>
                            <span className="text-sm text-gray-700">{item.title}</span>
                          </div>
                          {item.speaker && <p className="text-xs text-gray-400 mt-0.5">{item.speaker}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Intervenants */}
            {activeTab === 'speakers' && hasSpeakers && (
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="tab-speakers-content">
                <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Barlow', sans-serif" }}>Intervenants</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {speakers.map((s, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[#eaf6fc] transition-colors" data-testid={`speaker-${i}`}>
                      <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0" style={{ background: `linear-gradient(135deg, ${cfg.color}, #1e3a5f)` }}>
                        {s.name.split(' ').slice(-1)[0][0]}{s.name.split(' ').slice(-2, -1)[0]?.[0] || ''}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate" style={{ fontFamily: "'Barlow', sans-serif" }}>{s.name}</h3>
                        <p className="text-xs text-gray-500">{s.role}</p>
                        {s.topic && <p className="text-xs mt-0.5" style={{ color: cfg.color }}>{s.topic}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab: Galerie (past events, pro members only) */}
            {activeTab === 'galerie' && (
              <GalleryTabContent eventId={id} user={user} />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">

            {/* Registration card */}
            {upcoming && (
              <div className="bg-white rounded-xl p-6 shadow-sm border-t-4" style={{ borderTopColor: cfg.color }} data-testid="registration-card">
                <h3 className="text-base font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>Inscription</h3>
                {event.registration_open ? (
                  <>
                    {registered ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm font-medium" data-testid="registered-msg">
                        <UserCheck size={16} /> Vous êtes inscrit(e) !
                      </div>
                    ) : (
                      <button onClick={handleRegister} disabled={registering} className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-white font-bold text-sm transition-all hover:scale-[1.02] hover:shadow-lg disabled:opacity-50" style={{ background: cfg.color }} data-testid="register-btn">
                        {registering ? 'Inscription...' : <><ArrowRight size={14} /> S'inscrire</>}
                      </button>
                    )}
                    {regError && <p className="mt-2 text-xs text-red-500">{regError}</p>}
                    {event.registration_deadline && (
                      <p className="text-xs text-gray-400 mt-3 text-center">Date limite : {formatDateFr(event.registration_deadline)}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-2">Inscriptions fermées</p>
                )}
                {event.registrations_count > 0 && <p className="text-xs text-gray-400 mt-2 text-center">{event.registrations_count} inscrit(s)</p>}
              </div>
            )}

            {/* Info card */}
            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="info-card">
              <h3 className="text-base font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>Informations</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 text-sm">
                  <Calendar size={16} className="mt-0.5 flex-shrink-0" style={{ color: cfg.color }} />
                  <div><p className="text-xs text-gray-400">Date</p><p className="text-gray-800 font-medium">{dateRange}</p></div>
                </div>
                {event.location && (
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin size={16} className="mt-0.5 flex-shrink-0" style={{ color: cfg.color }} />
                    <div><p className="text-xs text-gray-400">Lieu</p><p className="text-gray-800 font-medium">{event.location}</p></div>
                  </div>
                )}
                {event.location_type === 'online' && event.online_link && (
                  <div className="flex items-start gap-3 text-sm">
                    <Video size={16} className="mt-0.5 flex-shrink-0" style={{ color: cfg.color }} />
                    <div><p className="text-xs text-gray-400">Lien</p><a href={event.online_link} target="_blank" rel="noopener noreferrer" className="text-[#35AFE4] hover:underline text-xs">Rejoindre la session</a></div>
                  </div>
                )}
                {event.max_participants && (
                  <div className="flex items-start gap-3 text-sm">
                    <Users size={16} className="mt-0.5 flex-shrink-0" style={{ color: cfg.color }} />
                    <div><p className="text-xs text-gray-400">Capacité</p><p className="text-gray-800 font-medium">{event.max_participants} participants max.</p></div>
                  </div>
                )}
              </div>
            </div>

            {/* Speakers mini */}
            {hasSpeakers && activeTab !== 'speakers' && (
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="speakers-sidebar">
                <h3 className="text-base font-bold text-gray-900 mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Intervenants</h3>
                <div className="space-y-2.5">
                  {speakers.slice(0, 3).map((s, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: cfg.color }}>
                        {s.name.split(' ').slice(-1)[0][0]}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-gray-900 truncate">{s.name}</p>
                        <p className="text-[10px] text-gray-400">{s.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {speakers.length > 3 && (
                  <button onClick={() => setActiveTab('speakers')} className="mt-2 text-xs text-[#35AFE4] hover:underline">Voir tous les intervenants</button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventDetailPage;
